import {
  CertificateSummary,
  CertificateStatus,
  capitalizeFirstLetterEachWord,
  localToUtc,
  stringToDate,
} from '@vcd-ecm-ui/Common';
import { GenericGridRowDataTemplate } from '@vcd-ecm-ui/GenericGrid';

// This is the Native Data Type used in the Grid
export declare type GridDataNativeType = CertificateSummary;

// This is the Data for Each Grid Row
export default class GridRowData implements GenericGridRowDataTemplate {
  certificateId: number;
  ecwCertificateId: string;
  uuid: string;
  created: Date;
  expirationDate: Date;
  jurisdiction: string;
  reason: string;
  source: string;
  priority: string;
  owner: string;
  certificateScope: string;
  action: string;
  effDate: Date;
  endDate: Date;
  status: CertificateStatus;
  sellerCode: string;
  additionalDocumentation: boolean;

  key: string; // Unique Key for Each Row - Required by VtxTable
  rowData: GridDataNativeType;

  constructor(rowData: GridDataNativeType) {
    this.rowData = rowData;
    this.setKey();

    this.setEcwCertificateId();
    this.setStatus();
    this.setEffDate();
    this.setJurisdiction();
    this.setCreated();
    this.setAdditionalDocumentation();
    this.setEndDate();
    this.setExpirationDate();
    this.setCertificateScope();
    this.setSellerCode();

    const proxyHandler = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      get: (instance: GridRowData, prop: string): any => ((prop in instance) && ((instance as any)[prop] !== undefined)
        ? (instance as any)[prop] // eslint-disable-line @typescript-eslint/no-explicit-any
        : (rowData as any)[prop]), // eslint-disable-line @typescript-eslint/no-explicit-any
    };

    const proxy = new Proxy(this, proxyHandler);

    return proxy;
  }

  public static fromData(rowData: GridDataNativeType): GridRowData {
    return new GridRowData(rowData);
  }

  private setKey(): void {
    this.key = this.rowData.uuid;
  }

  private setEcwCertificateId(): void {
    this.ecwCertificateId = this.rowData.ecwCertificateId ? this.rowData.ecwCertificateId.toString() : '--';
  }

  private setStatus(): void {
    this.status = this.rowData.status && this.rowData.status.state ? this.rowData.status.state : CertificateStatus.Pending;
  }

  private setCreated(): void {
    // Date already in UTC Format
    this.created = stringToDate(this.rowData.createDate);
  }

  private setEffDate(): void {
    this.effDate = localToUtc(stringToDate(this.rowData.certificateEffDate));
  }

  private setSellerCode(): void {
    this.sellerCode = this.rowData.seller;
  }

  private setJurisdiction(): void {
    if (this.rowData.jurisdictionReasonTypeMap) {
      this.jurisdiction = this.rowData.jurisdictionReasonTypeMap.split(',')
        .map((jurisdictionReasonType: string) => jurisdictionReasonType.split('|')[0])
        .join(',');
      const reasonTypes = this.rowData.jurisdictionReasonTypeMap.split(',').reduce(
        (reasonMap: Map<string, string>, jurisdictionReasonType: string) => {
          reasonMap.set(jurisdictionReasonType.split('|')[1], '');
          return reasonMap;
        }, new Map(),
      );
      this.reason = Array.from(reasonTypes.keys()).join(',');
    }
  }

  private setAdditionalDocumentation(): void {
    this.additionalDocumentation = this.rowData ? this.rowData.imageIdCount > 1 : false;
  }

  private setEndDate(): void {
    this.endDate = localToUtc(stringToDate(this.rowData.certificateEndDate));
  }

  private setExpirationDate(): void {
    this.expirationDate = localToUtc(stringToDate(this.rowData.expirationDate));
  }

  private setCertificateScope(): void {
    this.certificateScope = capitalizeFirstLetterEachWord(this.rowData.certificateScope);
  }
}
